import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

import { forceControlOffScope } from './shared/forceControlOffScope';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['signFormLastNameInitialExperiment'];

type Params = {
	experiment: Experiment;
	signed: boolean;
};

/**
 * Treatment is deferred to the front-end (`useTreatLastNameInitialExperiment`), so `treated` is always
 * forced to `false` here — the middle-end can return `treated: true` for a returning session even with
 * `treat: false`.
 *
 * Deferral is the whole point of this experiment's measurement design: the analysis scopes the treated
 * population to sessions that actually rendered the recently signed carousel, which is only known at
 * render time (the carousel needs `MIN_SIGNERS_TO_SHOW` signers and only exists inside the mobile sign
 * modal). Treating here would enroll every petition-details pageview instead.
 */
export function normalizeSignFormLastNameInitialExperiment({ experiment, signed }: Params): Experiment {
	if (!shouldRunExperiment({ experiment, signed })) {
		return forceControlOffScope(experiment);
	}

	return { ...experiment, treated: false };
}

/**
 * No device or petition check here on purpose — the carousel only renders inside the mobile sign modal,
 * so the surface itself defines the audience. A second gate would be a competing source of truth.
 *
 * `signed`, though, does NOT mirror a render condition. Signed visitors still reach the carousel when
 * `unauthenticatedSignFormEnabled` is on: MobileActionButton only swaps in the share flow for
 * `signed && !unauthenticatedSignFormEnabled`, and `RecentSignersFeed` has no `signed` check of its own.
 * So this knowingly gives up a slice of real carousel-viewing traffic.
 *
 * Kept because it is symmetric across arms (so it cannot bias the comparison), and because a guest who
 * buckets into a treatment arm and turns out on refetch to have already signed would otherwise watch
 * names change mid-modal. The cost is statistical power, which the run-window sizing has to absorb.
 */
function shouldRunExperiment({ experiment, signed }: Params): boolean {
	return hasRealAssignment(experiment) && !signed;
}

/**
 * Any `fallback` means the variation did not come from a real experiment-service assignment — the FCM kill
 * switch (`disabled`), an unreachable service (`fetch-error`), or an experiment not yet registered
 * (`not-found`). All of them are out of scope, not just the kill switch.
 */
function hasRealAssignment(experiment: Experiment): boolean {
	return !experiment.fallback;
}
